import { type FieldSize, Badge } from "@mdotm/shadcn/legacy/components";
import { type ClassList, toClassListRecord } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { ReactNode } from "react";
import { SentimentSymbol } from "./icons/SentimentSymbol";
import type { IndicatorType, SentimentType } from "./sentiment";
import { match } from "ts-pattern";
import { categorizeStringAsSentiment } from "$root/functional-areas/commentary/sentiment-badges";
import { alwaysArray } from "@mdotm/shadcn/legacy/utils";

export function SentimentBadge(props: {
	size?: FieldSize;
	children: ReactNode;
	className?: ClassList;
	indicator: IndicatorType;
	sentiment: SentimentType;
}): React.JSX.Element {
	const { className, sentiment, indicator, children, size = "small" } = props;

	return (
		<Badge
			size={size}
			className={{ capitalize: true, ...toClassListRecord(className) }}
			backgroundColor={match({ indicator: props.indicator, sentiment: props.sentiment })
				.returnType<string>()
				.with({ indicator: "driver", sentiment: "super-positive" }, () => themeCSSVars.palette_P50)
				.with({ indicator: "driver", sentiment: "positive" }, () => themeCSSVars.palette_P50)
				.with({ indicator: "driver", sentiment: "neutral" }, () => themeCSSVars.palette_S50)
				.with({ indicator: "driver", sentiment: "negative" }, () => themeCSSVars.palette_graph_R50)
				.with({ indicator: "driver", sentiment: "super-negative" }, () => themeCSSVars.palette_graph_R50)

				.with({ indicator: "regime", sentiment: "super-positive" }, () => themeCSSVars.palette_P50)
				.with({ indicator: "regime", sentiment: "positive" }, () => themeCSSVars.palette_P50)
				.with({ indicator: "regime", sentiment: "neutral" }, () => themeCSSVars.palette_A50)
				.with({ indicator: "regime", sentiment: "negative" }, () => themeCSSVars.palette_graph_R50)
				.with({ indicator: "regime", sentiment: "super-negative" }, () => themeCSSVars.palette_graph_R50)

				.with({ indicator: "positioning", sentiment: "super-positive" }, () => themeCSSVars.palette_P100)
				.with({ indicator: "positioning", sentiment: "positive" }, () => themeCSSVars.palette_P50)
				.with({ indicator: "positioning", sentiment: "neutral" }, () => themeCSSVars.palette_S50)
				.with({ indicator: "positioning", sentiment: "negative" }, () => themeCSSVars.palette_graph_R50)
				.with({ indicator: "positioning", sentiment: "super-negative" }, () => themeCSSVars.palette_graph_R100)
				.run()}
			color={themeCSSVars.palette_N600}
			leftContent={<SentimentSymbol sentiment={sentiment} indicator={indicator} className="w-full h-full" />}
		>
			{children}
		</Badge>
	);
}

export function AutoSentimentBadge(props: { children?: string | string[]; size?: FieldSize }): React.JSX.Element {
	const children = alwaysArray(props.children ?? []);
	return (
		<ForEach collection={children}>
			{(item) => {
				const data = categorizeStringAsSentiment(item);
				if (!data) {
					return item;
				}
				return (
					<SentimentBadge size={props.size} {...data}>
						{item}
					</SentimentBadge>
				);
			}}
		</ForEach>
	);
}
